import { trackEvent } from "@/utils/analytics";

export const businessFaqs = [
  {
    question: "How does the first $10,000 free offer work?",
    answer:
      "Your first $10,000 in transfers costs nothing. Zero fees, no credit card required to start. Standard pricing of 2% per transaction only kicks in after that threshold.",
  },
  {
    question: "How fast do recipients get paid?",
    answer:
      "Under 5 minutes in 100+ countries. No bank wire delays, no correspondent bank fees, no 3-5 business day waits. USDC moves on the blockchain — it settles in minutes regardless of where your recipient is.",
  },
  {
    question: "What do recipients need to receive payment?",
    answer:
      "Just a phone number. Flip automatically creates a non-custodial wallet linked to their number — they don't need to set anything up, download an app, or share a wallet address. They receive USDC and control their own funds from day one.",
  },
  {
    question: "What does it cost after the free $10K?",
    answer:
      "2% per transaction on Standard. 1.6% on Business (over $15K/month). Custom Enterprise pricing above $150K/month. No setup fees, no monthly minimums, no per-seat charges.",
  },
  {
    question: "Why USDC and not a bank transfer?",
    answer:
      "Banks don't just charge the fee they quote — they bury additional costs inside the FX markup, correspondent bank deductions, and receiving fees. By the time money arrives, your recipient gets less than you sent. USDC transfers settle at a fixed amount with a single transparent fee. What you send is what they get.",
  },
  {
    question: "Who is Flip built for?",
    answer:
      "Businesses that pay contractors, remote teams, or freelancers internationally and want a faster, cheaper alternative to bank wires. Particularly useful if your team already operates in stablecoins, or if you're paying people in countries where USDC is preferable to volatile local currencies.",
  },
  {
    question: "Who operates Flip?",
    answer:
      "Flip Company AS, based in Oslo, Norway. Operating under applicable financial regulations for stablecoin payment services.",
  },
  {
    question: "Can I white-label Flip?",
    answer:
      "Yes. We offer white-label options for businesses that want to run global USDC payouts under their own brand. Talk to us about scope and pricing.",
  },
];

type Variant = "navy" | "beige";

interface Props {
  page: "home" | "lp-x" | "lp-business-payouts" | "business-payments";
  variant?: Variant;
  title?: string;
}

const BusinessFaq = ({ page, variant = "navy", title = "Frequently asked questions" }: Props) => {
  const isNavy = variant === "navy";
  const sectionBg = isNavy ? "bg-[#1e1e3c]" : "bg-[#eeecdf]";
  const headingColor = isNavy ? "text-white" : "text-[#1e1e3c]";
  const cardBg = isNavy ? "bg-[#eeecdf]" : "bg-white";
  const qColor = "text-[#1e1e3c]";
  const aColor = "text-[#1e1e3c]/75";

  const handleToggle = (question: string, open: boolean) => {
    if (open) {
      trackEvent("faq_open", { page, question });
    }
  };

  return (
    <section className={`${sectionBg} py-20 lg:py-24`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className={`text-3xl lg:text-4xl font-bold ${headingColor} text-center mb-12`}>
            {title}
          </h2>
          <div className="space-y-3">
            {businessFaqs.map((faq, i) => (
              <details
                key={i}
                className={`group ${cardBg} rounded-xl border border-[#1e1e3c]/10 px-6 open:shadow-sm`}
                onToggle={(e) => handleToggle(faq.question, (e.target as HTMLDetailsElement).open)}
              >
                <summary
                  role="button"
                  onClick={() => trackEvent("faq_click", { page, question: faq.question })}
                  className={`flex items-center justify-between gap-4 cursor-pointer list-none py-5 font-semibold ${qColor} text-sm sm:text-base`}
                >
                  <span>{faq.question}</span>
                  <span
                    className="shrink-0 w-6 h-6 rounded-full bg-[#1e1e3c]/5 flex items-center justify-center text-[#1e1e3c] transition-transform group-open:rotate-45 text-lg leading-none pointer-events-none"
                  >
                    +
                  </span>
                </summary>
                <p className={`${aColor} pb-5 leading-relaxed text-sm sm:text-[15px]`}>
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessFaq;
